import { notificationAPI } from './api';

const SERVICE_WORKER_PATH = '/service-worker.js';
const NOTIFICATION_PERMISSION_KEY = 'notificationPermissionRequested';
const NOTIFICATION_SUBSCRIPTION_KEY = 'notificationSubscriptionId';

// TypeScript type definitions for Safari Push Notification API
declare global {
  interface Window {
    safari?: {
      pushNotification?: {
        permission: (websitePushId: string) => {
          permission: 'default' | 'granted' | 'denied';
          deviceToken?: string;
        };
        requestPermission: (
          serverPushPackageUrl: string,
          websitePushId: string,
          userInfo: any,
          callback: (permission: {
            permission: 'default' | 'granted' | 'denied';
            deviceToken?: string;
          }) => void
        ) => void;
      };
    };
  }
}

interface PushSubscriptionKeys {
  p256dh: string;
  auth: string;
}

interface PushSubscription {
  endpoint: string;
  keys: PushSubscriptionKeys;
}

class NotificationService {
  private static instance: NotificationService;
  private vapidPublicKey: string | null = null;
  private registration: ServiceWorkerRegistration | null = null;

  private constructor() {}

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  /**
   * Detect if device is Mac or iOS
   */
  isMacOrIOS(): boolean {
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    
    // Check for iOS devices
    if (/iPad|iPhone|iPod/.test(ua)) {
      return true;
    }
    
    // Check for Mac
    if (/Mac/.test(platform) || /Mac/.test(ua)) {
      return true;
    }
    
    return false;
  }

  /**
   * Detect if browser is Safari
   */
  isSafari(): boolean {
    const ua = navigator.userAgent || '';
    return /Safari/.test(ua) && !/Chrome|Chromium|Edge|Firefox|Opera/.test(ua);
  }

  /**
   * Get Safari version from user agent
   * Returns version number or null if not Safari or version cannot be determined
   */
  getSafariVersion(): number | null {
    if (!this.isSafari()) {
      return null;
    }

    const ua = navigator.userAgent || '';
    // Safari version format: Version/16.4 or Version/17.0
    const versionMatch = ua.match(/Version\/(\d+)\.(\d+)/);
    if (versionMatch) {
      const major = parseInt(versionMatch[1], 10);
      const minor = parseInt(versionMatch[2], 10);
      // Convert to comparable number (e.g., 16.4 -> 16.4, 17.0 -> 17.0)
      return major + minor / 10;
    }

    return null;
  }

  /**
   * Check if Safari version is below 16.4
   * Safari 16.4+ supports Web Push API, so we use WebPush for those versions
   */
  isSafariBelow16_4(): boolean {
    const version = this.getSafariVersion();
    if (version === null) {
      return false; // Not Safari, so not below 16.4
    }
    return version < 16.4;
  }

  /**
   * Check if Safari Push Notification API is supported
   */
  isSafariPushNotificationSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'safari' in window &&
      window.safari !== undefined &&
      'pushNotification' in window.safari
    );
  }

  /**
   * Determine if we should use APNs (Apple Push Notification service)
   * Only Safari < 16.4 uses APNs via Safari Push Notification API
   * Safari 16.4+ and all other browsers use WebPush
   */
  shouldUseAPNs(): boolean {
    // Sadece Mac/iOS Safari 16.4 altı için APNs kullan
    // Safari 16.4+ Web Push API'yi destekler, bu yüzden WebPush kullanılır
    if (this.isMacOrIOS() && this.isSafari() && this.isSafariBelow16_4() && this.isSafariPushNotificationSupported()) {
      console.log('[NotificationService] Using APNs for Safari < 16.4');
      return true;
    }
    
    // Safari 16.4+ ve diğer tüm tarayıcılar WebPush kullanacak
    if (this.isSafari()) {
      const version = this.getSafariVersion();
      console.log(`[NotificationService] Using WebPush for Safari ${version !== null ? version : 'unknown'} (16.4+)`);
    }
    return false;
  }

  /**
   * Check if browser supports notifications
   */
  isSupported(): boolean {
    // For APNs, we only need Notification API
    if (this.shouldUseAPNs()) {
      return 'Notification' in window;
    }
    
    // For WebPush, we need Service Worker and PushManager
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  /**
   * Check if user has already been asked for notification permission
   */
  hasPermissionBeenRequested(): boolean {
    return localStorage.getItem(NOTIFICATION_PERMISSION_KEY) === 'true';
  }

  /**
   * Mark that permission has been requested
   */
  markPermissionRequested(): void {
    localStorage.setItem(NOTIFICATION_PERMISSION_KEY, 'true');
  }

  /**
   * Clear notification permission request status (for testing/debugging)
   */
  clearPermissionRequestStatus(): void {
    localStorage.removeItem(NOTIFICATION_PERMISSION_KEY);
    localStorage.removeItem(NOTIFICATION_SUBSCRIPTION_KEY);
    console.log('[NotificationService] Permission request status cleared');
  }

  /**
   * Check if user has an active subscription
   */
  async hasActiveSubscription(): Promise<boolean> {
    try {
      const response = await notificationAPI.getSubscriptions();
      const subscriptions = response.data?.data?.subscriptions || [];
      const subscriptionType = this.shouldUseAPNs() ? 'apns' : 'webpush';
      return subscriptions.some((sub: any) => sub.isActive && sub.type === subscriptionType);
    } catch (error) {
      console.error('Failed to check subscriptions:', error);
      return false;
    }
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      throw new Error('This browser does not support notifications');
    }

    const permission = await Notification.requestPermission();
    this.markPermissionRequested();
    return permission;
  }

  /**
   * Register service worker
   */
  async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers are not supported');
    }

    try {
      const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
      this.registration = registration;
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }

  /**
   * Get VAPID public key from server
   */
  async getVapidPublicKey(): Promise<string> {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    try {
      const response = await notificationAPI.getVapidPublicKey();
      this.vapidPublicKey = response.data?.data?.publicKey;
      
      if (!this.vapidPublicKey) {
        throw new Error('VAPID public key not available');
      }

      return this.vapidPublicKey;
    } catch (error) {
      console.error('Failed to get VAPID public key:', error);
      throw error;
    }
  }

  /**
   * Convert VAPID key to Uint8Array
   */
  urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe(): Promise<PushSubscription | null> {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported in this browser');
    }
    console.log('[NotificationService] isSupported:', this.isSupported());
    
    // Check permission
    let permission = this.getPermissionStatus();
    console.log('[NotificationService] Permission status:', permission);
    if (permission === 'default') {
      permission = await this.requestPermission();
    }
    console.log('[NotificationService] Permission status after request:', permission);
    
    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }

    // Register service worker if not already registered
    if (!this.registration) {
      await this.registerServiceWorker();
    }
    console.log('[NotificationService] Registration:', this.registration);

    if (!this.registration) {
      throw new Error('Service worker registration failed');
    }

    // CRITICAL: Wait for service worker to be fully activated
    if (this.registration.active?.state !== 'activated') {
      console.log('[NotificationService] Service worker not activated, waiting...');
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Service worker activation timeout'));
        }, 10000); // 10 second timeout

        const checkState = () => {
          if (this.registration?.active?.state === 'activated') {
            clearTimeout(timeout);
            resolve();
          } else if (this.registration?.installing) {
            this.registration.installing.addEventListener('statechange', () => {
              if (this.registration?.active?.state === 'activated') {
                clearTimeout(timeout);
                resolve();
              }
            });
          } else if (this.registration?.waiting) {
            // If there's a waiting service worker, activate it
            this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            this.registration.waiting.addEventListener('statechange', () => {
              if (this.registration?.active?.state === 'activated') {
                clearTimeout(timeout);
                resolve();
              }
            });
          } else {
            // Check periodically
            const interval = setInterval(() => {
              if (this.registration?.active?.state === 'activated') {
                clearInterval(interval);
                clearTimeout(timeout);
                resolve();
              }
            }, 100);
          }
        };

        checkState();
      });
    }

    console.log('[NotificationService] Service worker state:', this.registration.active?.state);
    console.log('[NotificationService] Service worker scope:', this.registration.scope);

    // Get VAPID public key
    const vapidPublicKey = await this.getVapidPublicKey();
    console.log('[NotificationService] VAPID public key:', vapidPublicKey);
    console.log('[NotificationService] VAPID key length:', vapidPublicKey.length);
    
    // Validate VAPID key format
    if (!vapidPublicKey || vapidPublicKey.length < 80) {
      throw new Error('Invalid VAPID public key format');
    }

    const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);
    console.log('[NotificationService] Application server key length:', applicationServerKey.length);
    
    // VAPID key should be 65 bytes (uncompressed) or 87 bytes (compressed)
    if (applicationServerKey.length !== 65 && applicationServerKey.length !== 87) {
      console.warn('[NotificationService] Unexpected VAPID key length:', applicationServerKey.length);
    }

    // Check if there's an existing subscription
    let existingSubscription = null;
    try {
      existingSubscription = await this.registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('[NotificationService] Found existing subscription');
      }
    } catch (checkError) {
      console.log('[NotificationService] Error checking existing subscription:', checkError);
    }
    console.log('[NotificationService] Existing subscription:', existingSubscription);

    // Subscribe to push with retry and better error handling
    let subscription;
    const maxRetries = 3;
    let retryCount = 0;
    let lastError: any = null;

    while (retryCount < maxRetries) {
      try {
        console.log(`[NotificationService] Attempting subscription (attempt ${retryCount + 1}/${maxRetries})...`);
        
        // Increase timeout for each retry: 30s, 45s, 60s
        const timeoutDuration = (retryCount + 1) * 30000;
        console.log(`[NotificationService] Using timeout of ${timeoutDuration / 1000}s for this attempt`);
        
        subscription = await Promise.race([
          this.registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey as BufferSource,
          }),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error(`Subscription timeout after ${timeoutDuration / 1000} seconds`)), timeoutDuration)
          )
        ]) as PushSubscription;

        console.log('[NotificationService] Subscription successful:', subscription);
        console.log('[NotificationService] Subscription endpoint:', subscription.endpoint);
        break; // Success, exit retry loop
        
      } catch (subscribeError: any) {
        retryCount++;
        lastError = subscribeError;
        console.error(`[NotificationService] Subscribe attempt ${retryCount} failed:`, subscribeError);
        console.error('[NotificationService] Error name:', subscribeError.name);
        console.error('[NotificationService] Error message:', subscribeError.message);
        if (subscribeError.stack) {
          console.error('[NotificationService] Error stack:', subscribeError.stack);
        }
        
        // If subscribe fails due to different applicationServerKey, unsubscribe and retry
        if (
          subscribeError.name === 'InvalidStateError' || 
          subscribeError.message?.includes('different applicationServerKey') ||
          subscribeError.message?.includes('already exists')
        ) {
          console.log('[NotificationService] Different VAPID key detected, unsubscribing old subscription...');
          
          // Unsubscribe existing subscription
          if (existingSubscription) {
            try {
              await existingSubscription.unsubscribe();
              console.log('[NotificationService] Successfully unsubscribed old subscription');
            } catch (unsubError) {
              console.error('[NotificationService] Error unsubscribing:', unsubError);
            }
          }

          // Also try to delete from backend if we have the subscription ID
          const subscriptionId = localStorage.getItem(NOTIFICATION_SUBSCRIPTION_KEY);
          if (subscriptionId) {
            try {
              await notificationAPI.deleteSubscription(subscriptionId);
              console.log('[NotificationService] Deleted subscription from backend');
            } catch (deleteError) {
              console.error('[NotificationService] Failed to delete subscription from backend:', deleteError);
            }
            localStorage.removeItem(NOTIFICATION_SUBSCRIPTION_KEY);
          }

          // Retry immediately after unsubscribe (don't count this as a retry)
          retryCount--;
          continue;
        }
        
        // For push service errors and timeouts, wait and retry
        if (
          subscribeError.name === 'AbortError' ||
          subscribeError.name === 'NetworkError' ||
          subscribeError.name === 'Error' ||
          subscribeError.message?.includes('push service error') ||
          subscribeError.message?.includes('Registration failed') ||
          subscribeError.message?.includes('timeout') ||
          subscribeError.message?.includes('Timeout')
        ) {
          if (retryCount < maxRetries) {
            const waitTime = retryCount * 2000; // 2s, 4s, 6s
            console.log(`[NotificationService] Push service error or timeout, retrying in ${waitTime}ms... (attempt ${retryCount}/${maxRetries})`);
            console.log('[NotificationService] This might be due to slow network or push service response time');
            
            // Before retrying, check if service worker is still active
            if (this.registration.active?.state !== 'activated') {
              console.log('[NotificationService] Service worker not active, re-registering...');
              await this.registerServiceWorker();
              // Wait a bit for activation
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
            
            await new Promise(resolve => setTimeout(resolve, waitTime));
            continue;
          } else {
            // Last retry failed - provide detailed error
            const errorDetails = {
              name: subscribeError.name,
              message: subscribeError.message,
              serviceWorkerState: this.registration.active?.state,
              serviceWorkerScope: this.registration.scope,
              vapidKeyLength: vapidPublicKey.length,
              applicationServerKeyLength: applicationServerKey.length,
            };
            console.error('[NotificationService] All retry attempts failed. Error details:', errorDetails);
            throw new Error(
              `Failed to subscribe to push notifications after ${maxRetries} attempts. ` +
              `Error: ${subscribeError.message}. ` +
              `This might be due to network issues or browser push service being unavailable. ` +
              `Please try again later or check your network connection.`
            );
          }
        }
        
        // Different error, rethrow immediately
        throw subscribeError;
      }
    }

    if (!subscription) {
      throw lastError || new Error('Failed to subscribe to push notifications');
    }

    console.log('[NotificationService] Subscription:', subscription);
    
    // Convert subscription to format expected by backend
    const subscriptionData: PushSubscription = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: this.arrayBufferToBase64(
          (subscription as any).getKey('p256dh') as ArrayBuffer
        ),
        auth: this.arrayBufferToBase64(
          (subscription as any).getKey('auth') as ArrayBuffer
        ),
      },
    };
    console.log('[NotificationService] Subscription data:', subscriptionData);
    
    // Send subscription to backend
    try {
      const response = await notificationAPI.subscribeWebPush(subscriptionData);
      const subscriptionId = response.data?.data?.id;
      
      if (subscriptionId) {
        localStorage.setItem(NOTIFICATION_SUBSCRIPTION_KEY, subscriptionId);
      }

      console.log('[NotificationService] Successfully subscribed to push notifications');
      return subscriptionData;
    } catch (error: any) {
      console.error('[NotificationService] Failed to send subscription to server:', error);
      // Unsubscribe locally if server subscription fails
      try {
        await (subscription as any).unsubscribe();
      } catch (unsubError) {
        console.log('[NotificationService] Error unsubscribing after server failure:', unsubError);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications (but don't call API - user requested no unsubscribe on logout)
   */
  async unsubscribe(): Promise<void> {
    if (!this.registration) {
      return;
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        localStorage.removeItem(NOTIFICATION_SUBSCRIPTION_KEY);
        console.log('Unsubscribed from push notifications');
      }
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      throw error;
    }
  }

  /**
   * Get current push subscription
   */
  async getSubscription(): Promise<PushSubscription | null> {
    if (!this.registration) {
      return null;
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      if (!subscription) {
        return null;
      }

      return {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(
            subscription.getKey('p256dh') as ArrayBuffer
          ),
          auth: this.arrayBufferToBase64(
            subscription.getKey('auth') as ArrayBuffer
          ),
        },
      };
    } catch (error) {
      console.error('Failed to get subscription:', error);
      return null;
    }
  }

  /**
   * Generate a unique device identifier for web browsers
   * Since web browsers don't provide device tokens directly,
   * we create a unique identifier based on browser and device info
   */
  private generateDeviceIdentifier(): string {
    // Try to get existing identifier from localStorage
    const existingId = localStorage.getItem('apns_device_identifier');
    if (existingId) {
      return existingId;
    }

    // Generate a unique identifier based on:
    // - User agent
    // - Platform
    // - Screen resolution
    // - Timezone
    // - Random component
    const components = [
      navigator.userAgent || '',
      navigator.platform || '',
      `${window.screen.width}x${window.screen.height}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      Date.now().toString(),
      Math.random().toString(36).substring(2, 15)
    ];

    const identifier = btoa(components.join('|')).substring(0, 64);
    localStorage.setItem('apns_device_identifier', identifier);
    return identifier;
  }

  /**
   * Subscribe to APNs notifications using Safari Push Notification API
   * This API provides real device tokens for Safari browsers
   */
  async subscribeAPNs(): Promise<boolean> {
    try {
      console.log('[NotificationService] Subscribing to APNs via Safari Push Notification API...');
      
      // Safari Push Notification API kontrolü
      if (!this.isSafariPushNotificationSupported()) {
        throw new Error('Safari Push Notification API is not supported');
      }

      // Website Push ID - environment variable'dan al veya config'den
      const websitePushId = process.env.REACT_APP_SAFARI_WEBSITE_PUSH_ID || 'web.com.winnomi.platform';
      // Server push package URL - backend endpoint'i
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const serverPushPackageUrl = process.env.REACT_APP_SAFARI_PUSH_PACKAGE_URL || `${apiBaseUrl}/notifications/pushPackage.pushpackage`;

      console.log('[NotificationService] Website Push ID:', websitePushId);
      console.log('[NotificationService] Server Push Package URL:', serverPushPackageUrl);

      // Mevcut izin durumunu kontrol et
      const permissionData = window.safari!.pushNotification!.permission(websitePushId);
      console.log('[NotificationService] Current permission:', permissionData.permission);

      // Device token'ı Promise olarak almak için wrapper
      return new Promise<boolean>((resolve, reject) => {
        if (permissionData.permission === 'default') {
          // Kullanıcıya izin iste
          console.log('[NotificationService] Requesting Safari push notification permission...');
          
          window.safari!.pushNotification!.requestPermission(
            serverPushPackageUrl,
            websitePushId,
            {}, // ekstra data (optional)
            async (permission) => {
              console.log('[NotificationService] Permission result:', permission.permission);
              
              if (permission.permission === 'granted') {
                const deviceToken = permission.deviceToken;
                if (!deviceToken) {
                  reject(new Error('Device token not received from Safari'));
                  return;
                }
                
                console.log('[NotificationService] Device token received:', deviceToken);
                
                // İzin verildiğini işaretle
                this.markPermissionRequested();
                
                try {
                  // Device token'ı backend'e gönder
                  const response = await notificationAPI.subscribeAPNs({
                    deviceToken: deviceToken
                  });
                  
                  const subscriptionId = response.data?.data?.id;
                  if (subscriptionId) {
                    localStorage.setItem(NOTIFICATION_SUBSCRIPTION_KEY, subscriptionId);
                  }

                  console.log('[NotificationService] Successfully subscribed to APNs with device token');
                  resolve(true);
                } catch (error: any) {
                  console.error('[NotificationService] Failed to send device token to backend:', error);
                  reject(error);
                }
              } else {
                console.log('[NotificationService] User denied Safari push notification permission');
                reject(new Error('Notification permission denied'));
              }
            }
          );
        } else if (permissionData.permission === 'granted') {
          // Zaten izin verilmiş, device token'ı al
          const deviceToken = permissionData.deviceToken;
          if (!deviceToken) {
            reject(new Error('Device token not available'));
            return;
          }
          
          console.log('[NotificationService] Permission already granted, device token:', deviceToken);
          
          // İzin verildiğini işaretle
          this.markPermissionRequested();
          
          // Device token'ı backend'e gönder
          notificationAPI.subscribeAPNs({
            deviceToken: deviceToken
          })
          .then((response) => {
            const subscriptionId = response.data?.data?.id;
            if (subscriptionId) {
              localStorage.setItem(NOTIFICATION_SUBSCRIPTION_KEY, subscriptionId);
            }
            console.log('[NotificationService] Successfully subscribed to APNs with existing device token');
            resolve(true);
          })
          .catch((error) => {
            console.error('[NotificationService] Failed to send device token to backend:', error);
            reject(error);
          });
        } else {
          // İzin reddedilmiş
          console.log('[NotificationService] Safari push notification permission denied');
          reject(new Error('Notification permission denied'));
        }
      });
    } catch (error: any) {
      console.error('[NotificationService] APNs subscription failed:', error);
      throw error;
    }
  }

  /**
   * Initialize notification service (register service worker for WebPush only)
   */
  async initialize(): Promise<void> {
    if (!this.isSupported()) {
      console.log('[NotificationService] Push notifications are not supported');
      return;
    }

    // Only register service worker for WebPush, not for APNs
    if (this.shouldUseAPNs()) {
      console.log('[NotificationService] Using APNs, service worker not needed');
      return;
    }

    try {
      await this.registerServiceWorker();
    } catch (error) {
      console.error('Failed to initialize notification service:', error);
    }
  }

  /**
   * Request notification permission and subscribe if granted
   * This is the main function to call when user logs in
   */
  async requestAndSubscribe(): Promise<boolean> {
    try {
      console.log('[NotificationService] requestAndSubscribe called');
      console.log('[NotificationService] Device type - Mac/iOS:', this.isMacOrIOS());
      console.log('[NotificationService] Browser - Safari:', this.isSafari());
      console.log('[NotificationService] Should use APNs:', this.shouldUseAPNs());
      
      // Check if browser supports notifications
      if (!this.isSupported()) {
        console.log('[NotificationService] Push notifications not supported');
        return false;
      }

      // Check if permission was already requested
      if (this.hasPermissionBeenRequested()) {
        console.log('[NotificationService] Permission was already requested');
        // Check if user already has permission granted
        const permission = this.getPermissionStatus();
        if (permission === 'granted') {
          // Try to subscribe if not already subscribed
          try {
            if (this.shouldUseAPNs()) {
              await this.subscribeAPNs();
            } else {
              await this.ensureSubscription();
            }
            return true;
          } catch (error) {
            console.log('[NotificationService] Failed to ensure subscription:', error);
          }
        }
        return false;
      }

      // Use APNs for Mac/iOS, WebPush for others
      if (this.shouldUseAPNs()) {
        console.log('[NotificationService] Using APNs for Mac/iOS device');
        await this.subscribeAPNs();
        console.log('[NotificationService] Successfully subscribed to APNs');
        return true;
      }

      // WebPush for other devices
      console.log('[NotificationService] Using WebPush for non-Mac/iOS device');
      
      // Ensure service worker is registered
      if (!this.registration) {
        console.log('[NotificationService] Registering service worker...');
        await this.registerServiceWorker();
      }

      if (!this.registration) {
        console.error('[NotificationService] Service worker registration failed');
        return false;
      }

      console.log('[NotificationService] Requesting notification permission...');
      // Request permission and subscribe
      await this.subscribe();
      console.log('[NotificationService] Successfully subscribed to WebPush notifications');
      return true;
    } catch (error: any) {
      console.error('[NotificationService] Failed to request and subscribe:', error);
      // Don't throw - just log the error
      return false;
    }
  }

  /**
   * Ensure subscription exists (subscribe if not already subscribed)
   */
  async ensureSubscription(): Promise<PushSubscription | null> {
    if (!this.registration) {
      await this.registerServiceWorker();
    }

    if (!this.registration) {
      throw new Error('Service worker not registered');
    }

    // Check if already subscribed
    const existingSubscription = await this.getSubscription();
    if (existingSubscription) {
      console.log('[NotificationService] Already subscribed');
      return existingSubscription;
    }

    // Subscribe if permission is granted
    const permission = this.getPermissionStatus();
    if (permission === 'granted') {
      return await this.subscribe();
    }

    throw new Error('Notification permission not granted');
  }

  /**
   * Helper: Convert ArrayBuffer to base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

export default NotificationService;

